import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Button,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Skeleton,
} from '@mui/material';
import { Add, Edit, Refresh, CheckCircle, Block } from '@mui/icons-material';
import { useMediaQuery, useTheme } from '@mui/material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { AppDispatch, RootState } from '../../../store/store';
import {
  fetchSubscriptionPlans,
  createSubscriptionPlan,
  updateSubscriptionPlan,
  activateSubscriptionPlan,
  deactivateSubscriptionPlan,
} from '../../../store/slices/superAdminSlice';
import {
  SubscriptionPlanDto,
  CreateSubscriptionPlan,
  UpdateSubscriptionPlan,
  SubscriptionPeriod,
  SubscriptionPeriodLabels,
} from '../../../types/superAdminTypes';

const toastOptions = { position: 'top-center' as const };
type SortDirection = 'asc' | 'desc';
type PlanSortKey = 'planName' | 'pricePerEmployee' | 'maxUsers' | 'period' | 'tenantCount' | 'isActive';

const SubscriptionPlansPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { subscriptionPlans, loading, error } = useSelector((state: RootState) => state.superAdmin);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlanDto | null>(null);
  const [form, setForm] = useState<CreateSubscriptionPlan>({
    planName: '',
    pricePerEmployee: 0,
    maxUsers: 50,
    period: SubscriptionPeriod.Yearly,
  });
  const [sortBy, setSortBy] = useState<PlanSortKey>('planName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // const [errors, setErrors] = useState({
  //   pricePerEmployee: "",
  //   maxUsers: ""
  // });

  const fetchData = useCallback(() => {
    dispatch(fetchSubscriptionPlans());
  }, [dispatch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    setPage((currentPage) => {
      const maxPage = Math.max(0, Math.ceil(subscriptionPlans.length / rowsPerPage) - 1);
      return currentPage > maxPage ? maxPage : currentPage;
    });
  }, [rowsPerPage, subscriptionPlans.length]);

  const sortedSubscriptionPlans = useMemo(() => {
    const getSortValue = (plan: SubscriptionPlanDto, key: PlanSortKey): string | number => {
      if (key === 'planName') return plan.planName.toLowerCase();
      if (key === 'pricePerEmployee') return plan.pricePerEmployee;
      if (key === 'maxUsers') return plan.maxUsers;
      if (key === 'period') return plan.period ?? SubscriptionPeriod.Yearly;
      if (key === 'tenantCount') return plan.tenantCount;
      return plan.isActive ? 1 : 0;
    };

    return [...subscriptionPlans].sort((a, b) => {
      const first = getSortValue(a, sortBy);
      const second = getSortValue(b, sortBy);
      const direction = sortDirection === 'asc' ? 1 : -1;

      if (first < second) return -1 * direction;
      if (first > second) return 1 * direction;

      return a.planName.localeCompare(b.planName);
    });
  }, [sortBy, sortDirection, subscriptionPlans]);

  const paginatedSubscriptionPlans = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedSubscriptionPlans.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, sortedSubscriptionPlans]);

  const handleSort = (key: PlanSortKey) => {
    setSortDirection((currentDirection) => (sortBy === key && currentDirection === 'asc' ? 'desc' : 'asc'));
    setSortBy(key);
    setPage(0);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderSortLabel = (key: PlanSortKey, label: string) => (
    <TableSortLabel
      active={sortBy === key}
      direction={sortBy === key ? sortDirection : 'asc'}
      onClick={() => handleSort(key)}
      sx={{ fontWeight: 'bold' }}
    >
      {label}
    </TableSortLabel>
  );

  const handleOpenCreate = () => {
    setEditingPlan(null);
    setForm({ planName: '', pricePerEmployee: 0, maxUsers: 50, period: SubscriptionPeriod.Yearly });
    setOpenDialog(true);
  };

  const handleOpenEdit = (plan: SubscriptionPlanDto) => {
    setEditingPlan(plan);
    setForm({
      planName: plan.planName,
      pricePerEmployee: plan.pricePerEmployee,
      maxUsers: plan.maxUsers,
      period: plan.period ?? SubscriptionPeriod.Yearly,
    });
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingPlan) {
        const updateData: UpdateSubscriptionPlan = { ...form, id: editingPlan.id, isActive: editingPlan.isActive };
        await dispatch(updateSubscriptionPlan({ id: editingPlan.id, data: updateData })).unwrap();
        toast.success('Plan updated successfully', toastOptions);
      } else {
        await dispatch(createSubscriptionPlan(form)).unwrap();
        toast.success('Plan created successfully', toastOptions);
      }
      setOpenDialog(false);
      fetchData();
    } catch (err: any) {
      toast.error(err || 'Operation failed', toastOptions);
    }
  };


  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d{0,7}$/.test(value)) {
      setForm({
        ...form,
        pricePerEmployee: value === '' ? 0 : Number(value)
      });
      // setErrors({
      //   ...errors,
      //   pricePerEmployee: value.length === 7 ? "Max 7 digits allowed" : ""
      // });
    }
  };


  const handleUsersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d{0,5}$/.test(value)) {
      setForm({
        ...form,
        maxUsers: value === '' ? 0 : Number(value)
      });
      // setErrors({
      //   ...errors,
      //   maxUsers: value.length === 5 ? "Max 5 digits allowed" : ""
      // });
    }
  };

  const handleStatusChange = async (plan: SubscriptionPlanDto) => {
    const shouldActivate = !plan.isActive;
    const result = await Swal.fire({
      title: `${shouldActivate ? 'Activate' : 'Deactivate'} Plan?`,
      text: shouldActivate
        ? 'This will re-enable the subscription plan and make it available to tenants.'
        : 'This will hide the subscription plan from new tenant selections.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: shouldActivate ? '#3085d6' : '#ed6c02',
      confirmButtonText: `Yes, ${shouldActivate ? 'Activate' : 'Deactivate'}`,
    });
    if (result.isConfirmed) {
      try {
        if (shouldActivate) {
          await dispatch(activateSubscriptionPlan(plan.id)).unwrap();
        } else {
          await dispatch(deactivateSubscriptionPlan(plan.id)).unwrap();
        }
        toast.success(`Plan ${shouldActivate ? 'activated' : 'deactivated'}`, toastOptions);
        fetchData();
      } catch (err: any) {
        toast.error(err || `Failed to ${shouldActivate ? 'activate' : 'deactivate'} plan`, toastOptions);
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 0 }, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>Subscription Plans</Typography>
          <Typography variant="body2" color="text.secondary">Manage pricing plans and features</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', whiteSpace: 'nowrap' }}>
            Add Plan
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Plans as Cards */}
      {/* <Grid container spacing={3}>
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
              <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
            </Grid>
          ))
        ) : subscriptionPlans.length === 0 ? (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
              <Typography color="text.secondary">No subscription plans found</Typography>
            </Paper>
          </Grid>
        ) : (
          subscriptionPlans.map((plan) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={plan.id}>
              <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">{plan.planName}</Typography>
                  </Box>
                  <Chip label={plan.isActive ? 'Active' : 'Inactive'} color={plan.isActive ? 'success' : 'default'} size="small" />
                </Box>

                <Typography variant="h4" fontWeight="bold" color="primary" sx={{ mb: 1, fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
                  ₹{plan.pricePerEmployee.toLocaleString()}
                  <Typography component="span" variant="body2" color="text.secondary"> / employee</Typography>
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Max Users: <strong>{plan.maxUsers}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Subscribers: <strong>{plan.tenantCount}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Period: <strong>{SubscriptionPeriodLabels[plan.period ?? SubscriptionPeriod.Yearly]}</strong>
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => handleOpenEdit(plan)} fullWidth>Edit</Button>
                  <Button
                    size="small"
                    variant={plan.isActive ? 'outlined' : 'contained'}
                    color={plan.isActive ? 'warning' : 'success'}
                    startIcon={plan.isActive ? <Block /> : <CheckCircle />}
                    onClick={() => handleStatusChange(plan)}
                    fullWidth
                  >
                    {plan.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))
        )}
      </Grid> */}

      {/* Also show as table for detailed view */}
      {loading || subscriptionPlans.length > 0 ? (
        <TableContainer component={Paper} sx={{ mt: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Table>
              <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sortDirection={sortBy === 'planName' ? sortDirection : false}>{renderSortLabel('planName', 'Plan Name')}</TableCell>
                <TableCell sortDirection={sortBy === 'pricePerEmployee' ? sortDirection : false}>{renderSortLabel('pricePerEmployee', 'Price/Employee')}</TableCell>
                <TableCell sortDirection={sortBy === 'maxUsers' ? sortDirection : false}>{renderSortLabel('maxUsers', 'Max Users')}</TableCell>
                <TableCell sortDirection={sortBy === 'period' ? sortDirection : false}>{renderSortLabel('period', 'Period')}</TableCell>
                <TableCell sortDirection={sortBy === 'tenantCount' ? sortDirection : false}>{renderSortLabel('tenantCount', 'Subscribers')}</TableCell>
                <TableCell sortDirection={sortBy === 'isActive' ? sortDirection : false}>{renderSortLabel('isActive', 'Status')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: Math.min(rowsPerPage, 5) }).map((_, index) => (
                  <TableRow key={`subscription-plan-loading-${index}`}>
                    <TableCell colSpan={7}>
                      <Skeleton variant="text" height={32} />
                    </TableCell>
                  </TableRow>
                ))
              ) : paginatedSubscriptionPlans.map((plan) => (
                <TableRow key={plan.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{plan.planName}</Typography></TableCell>
                  <TableCell>₹{plan.pricePerEmployee.toLocaleString()}</TableCell>
                  <TableCell>{plan.maxUsers}</TableCell>
                  <TableCell>{SubscriptionPeriodLabels[plan.period ?? SubscriptionPeriod.Yearly]}</TableCell>
                  <TableCell>{plan.tenantCount}</TableCell>
                  <TableCell><Chip label={plan.isActive ? 'Active' : 'Inactive'} color={plan.isActive ? 'success' : 'default'} size="small" /></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => handleOpenEdit(plan)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color={plan.isActive ? 'warning' : 'success'}
                        startIcon={plan.isActive ? <Block /> : <CheckCircle />}
                        onClick={() => handleStatusChange(plan)}
                      >
                        {plan.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {subscriptionPlans.length > 0 && (
            <TablePagination
              component="div"
              count={sortedSubscriptionPlans.length}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          )}
        </TableContainer>
      ) : (
        <Paper sx={{ p: 4, mt: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography color="text.secondary">No subscription plans found</Typography>
        </Paper>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create Plan'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Plan Name" value={form.planName} onChange={(e) => setForm({ ...form, planName: e.target.value })} required />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Price Per Employee (₹)"
                type="text"
                value={form.pricePerEmployee}
                onChange={handlePriceChange}
                //error={!!errors.pricePerEmployee}
                //helperText={errors.pricePerEmployee}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Max Users"
                type="text"
                value={form.maxUsers}
                onChange={handleUsersChange}
                //error={!!errors.maxUsers}
                //helperText={errors.maxUsers}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                fullWidth
                label="Period"
                value={form.period ?? SubscriptionPeriod.Yearly}
                onChange={(e) => setForm({ ...form, period: Number(e.target.value) as SubscriptionPeriod })}
                SelectProps={{ native: true }}
              >
                <option value={SubscriptionPeriod.Monthly}>Monthly</option>
                <option value={SubscriptionPeriod.Quarterly}>Quarterly</option>
                <option value={SubscriptionPeriod.HalfYearly}>Half Yearly</option>
                <option value={SubscriptionPeriod.Yearly}>Yearly</option>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            {editingPlan ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionPlansPage;
